// Profile icons (decision 84): instead of renaming themselves, people pick one of these 16×16
// pixel-art sprites, drawn in the same 8-bit style as Roary the lion (apps/web/src/lion-sprites.ts).
// One character per pixel, "." is see-through, colours from SPRITE_PALETTE (CHA navy, blue, gold
// and salmon plus a few extras). The API keeps only the id (users.avatar) and checks it against
// SPRITE_IDS; the web app draws the pixels. Every sprite has a plain name, used as its alt text.

export const SPRITE_PALETTE: Record<string, string> = {
  K: "#020c1f", // outline
  N: "#041e42", // CHA Navy
  B: "#134b8d", // CHA Medium Blue
  b: "#2a68b3", // lighter blue
  L: "#8fb4e3", // sky
  G: "#f0ad42", // CHA Gold
  g: "#b8791f", // dark gold
  Y: "#ffd98a", // pale gold
  S: "#d46048", // CHA Salmon
  s: "#9e3f2c", // dark salmon
  R: "#d8333f", // red
  O: "#e8833a", // orange
  W: "#ffffff", // white
  w: "#c9d6ea", // light gray
  D: "#909e9f", // CHA Gray
  d: "#5e6b6c", // dark gray
  E: "#5bc48a", // green
  e: "#2e7d5b", // dark green
  P: "#f4a7b9", // pink
  p: "#8e5cc2", // purple
  q: "#c6a4ec", // light purple
  n: "#a0662f", // brown
  c: "#5c3a1a", // dark brown
  T: "#55d0e0", // teal
  t: "#1f8a9e", // dark teal
};

export const SPRITE_SIZE = 16;

export interface Sprite {
  id: string;
  /** What it is, for alt text and the picker's labels ("Lion cub"). */
  name: string;
  rows: readonly string[];
}

export const SPRITES: readonly Sprite[] = [
  // ---- animals ----
  {
    id: "lion-cub",
    name: "Lion cub",
    rows: [
      "....KKKKKKKK....",
      "..KKggggggggKK..",
      ".KggggGggGggggK.",
      ".KgggKKKKKKgggK.",
      "KgggKGGGGGGKgggK",
      "KggKGGGGGGGGKggK",
      "KggKGKGGGGKGKggK",
      "KggKGKGGGGKGKggK",
      "KggKSGGYYGGSKggK",
      "KggKGGYssYGGKggK",
      ".KgKGGYYYYGGKgK.",
      ".KggKGYKKYGKggK.",
      "..KggKGGGGKggK..",
      "...KggKKKKggK...",
      "....KKggggKK....",
      "......KKKK......",
    ],
  },
  {
    id: "fox",
    name: "Fox",
    rows: [
      "................",
      ".KK..........KK.",
      ".KOK........KOK.",
      ".KsOK......KOsK.",
      ".KssOKKKKKKOssK.",
      "KOOOOOOOOOOOOOOK",
      "KOOOKOOOOOOKOOOK",
      "KOOOKOOOOOOKOOOK",
      "KWOOOOOOOOOOOOWK",
      ".KWWOOOOOOOOWWK.",
      ".KWWWWOOOOWWWWK.",
      "..KWWWWWWWWWWK..",
      "...KWWWWWWWWK...",
      "....KWWWWWWK....",
      ".....KWKKWK.....",
      "......KKKK......",
    ],
  },
  {
    id: "owl",
    name: "Owl",
    rows: [
      "................",
      ".KK..........KK.",
      ".KnK........KnK.",
      ".KnnKKKKKKKKnnK.",
      ".KnnnnnnnnnnnnK.",
      "KnWWWWnnnnWWWWnK",
      "KnWKKWnnnnWKKWnK",
      "KnWKKWnGGnWKKWnK",
      "KnWWWWnGGnWWWWnK",
      "KnnnnnnnnnnnnnnK",
      "KnnYYYYYYYYYYnnK",
      ".KnYnYYnnYYnYnK.",
      ".KnYYYYYYYYYYnK.",
      "..KnYnYYYYnYnK..",
      "...KKGKKKKGKK...",
      "................",
    ],
  },
  {
    id: "cat",
    name: "Cat",
    rows: [
      "................",
      "................",
      ".KK..........KK.",
      ".KDK........KDK.",
      ".KPDK......KDPK.",
      ".KPDDKKKKKKDDPK.",
      ".KDDDDdDDdDDDDK.",
      "KDDDDDDdDDDDDDDK",
      "KDDDEKDDDDEKDDDK",
      "KDDDEKDDDDEKDDDK",
      "wKDDDDDPPDDDDDKw",
      "KwDDDDKDDKDDDDwK",
      ".KDDDDDKKDDDDDK.",
      "..KDDDDDDDDDDK..",
      "...KKKKKKKKKK...",
      "................",
    ],
  },
  {
    id: "puppy",
    name: "Puppy",
    rows: [
      "................",
      "....KKKKKKKK....",
      "..KKnnnnnnnnKK..",
      ".KcKnnnnnnnnKcK.",
      "KccKnnnnnnnnKccK",
      "KccKnKnnnnKnKccK",
      "KccKnKnnnnKnKccK",
      "KccKnnnWWnnnKccK",
      "KccKnnWWWWnnKccK",
      ".KKKnWWKKWWnKKK.",
      "...KnWWWWWWnK...",
      "...KnnWKKWnnK...",
      "....KnnPPnnK....",
      ".....KKPPKK.....",
      "......KKKK......",
      "................",
    ],
  },
  {
    id: "bunny",
    name: "Bunny",
    rows: [
      "..KKKK....KKKK..",
      "..KWPK....KPWK..",
      "..KWPK....KPWK..",
      "..KWPK....KPWK..",
      "..KWPK....KPWK..",
      "..KWWKKKKKKWWK..",
      ".KWWWWWWWWWWWWK.",
      "KWWWWWWWWWWWWWWK",
      "KWWWKWWWWWWKWWWK",
      "KWWWKWWWWWWKWWWK",
      "KWPPWWWPPWWWPPWK",
      "KWWWWWKWWKWWWWWK",
      ".KWWWWWKKWWWWWK.",
      "..KWWWWWWWWWWK..",
      "...KKKKKKKKKK...",
      "................",
    ],
  },
  {
    id: "turtle",
    name: "Turtle",
    rows: [
      "................",
      "................",
      "................",
      "....KKKKK.......",
      "...KEeEeEK......",
      "..KEeEEEeEK.KKK.",
      ".KEeEEEEEeEKEEEK",
      ".KeEEEEEEEeKEKEK",
      "KeeeeeeeeeeKEEEK",
      "KYYYYYYYYYYKKKK.",
      ".KKEEKKKKEEKK...",
      "..KEEK..KEEK....",
      "..KKKK..KKKK....",
      "................",
      "................",
      "................",
    ],
  },
  {
    id: "frog",
    name: "Frog",
    rows: [
      "................",
      "................",
      "..KKKK....KKKK..",
      ".KWWWWK..KWWWWK.",
      ".KWKKWKKKKWKKWK.",
      ".KWKKWEEEEWKKWK.",
      "KEKWWEEEEEEWWKEK",
      "KEEEEEEEEEEEEEEK",
      "KEEEEEEEEEEEEEEK",
      "KEPEKEEEEEEKEPEK",
      "KEEEEKKKKKKEEEEK",
      ".KEEEEEEEEEEEEK.",
      "..KeeEEEEEEeeK..",
      ".KeeKKKKKKKKeeK.",
      ".KKK........KKK.",
      "................",
    ],
  },
  {
    id: "penguin",
    name: "Penguin",
    rows: [
      ".....KKKKKK.....",
      "....KNNNNNNK....",
      "...KNNNNNNNNK...",
      "..KNNWWNNWWNNK..",
      "..KNWKWWWWKWNK..",
      "..KNWWWGGWWWNK..",
      ".KNNWWGGGGWWNNK.",
      ".KNNWWWWWWWWNNK.",
      "KNNKWWWWWWWWKNNK",
      "KNKKWWWWWWWWKKNK",
      "KK.KWWWWWWWWK.KK",
      "...KNWWWWWWNK...",
      "...KNNWWWWNNK...",
      "....KNNNNNNK....",
      "...KGGKKKKGGK...",
      "...KKK....KKK...",
    ],
  },
  {
    id: "bear",
    name: "Bear",
    rows: [
      "................",
      ".KKK........KKK.",
      "KnnnK......KnnnK",
      "KncnKKKKKKKKncnK",
      "KnnnnnnnnnnnnnnK",
      ".KnnnnnnnnnnnnK.",
      "KnnnKnnnnnnKnnnK",
      "KnnnKnnnnnnKnnnK",
      "KnnnnnYYYYnnnnnK",
      "KnnnnYYKKYYnnnnK",
      "KnnnnYYKKYYnnnnK",
      ".KnnnYYYYYYnnnK.",
      ".KnnnnYKKYnnnnK.",
      "..KnnnnnnnnnnK..",
      "...KKKKKKKKKK...",
      "................",
    ],
  },
  {
    id: "panda",
    name: "Panda",
    rows: [
      "................",
      "..KK........KK..",
      ".KKKKKKKKKKKKKK.",
      ".KKWWWWWWWWWWKK.",
      ".KWWWWWWWWWWWWK.",
      "KWWWWWWWWWWWWWWK",
      "KWWKKKWWWWKKKWWK",
      "KWKKWKKWWKKWKKWK",
      "KWKKKKWWWWKKKKWK",
      "KWWKKWWWWWWKKWWK",
      "KWWWWWWKKWWWWWWK",
      "KWWWWWKWWKWWWWWK",
      ".KWWWWWKKWWWWWK.",
      "..KWWWWWWWWWWK..",
      "...KKKKKKKKKK...",
      "................",
    ],
  },
  {
    id: "dragon",
    name: "Dragon",
    rows: [
      "..K..........K..",
      "..KGK......KGK..",
      "..KGGK....KGGK..",
      "...KGGKKKKGGK...",
      "...KEEEEEEEEK...",
      "..KEEEEEEEEEEK..",
      "..KEWKEEEEWKEK..",
      "..KEKKEEEEKKEK..",
      "..KEEEEEEEEEEK..",
      "...KEEKEEKEEK...",
      "KK..KKWKKWKK..KK",
      "KeKKeEYYYYEeKKeK",
      "KeeKEEYYYYEEKeeK",
      "KeK.KEYYYYEK.KeK",
      ".K..KEEKKEEK..K.",
      "....KKK..KKK....",
    ],
  },
  {
    id: "unicorn",
    name: "Unicorn",
    rows: [
      "..........KK....",
      ".........KYK....",
      "........KGK.....",
      "...KK..KYK......",
      "..KpKKKKKK......",
      ".KpqKWWWWWK.....",
      ".KqpKWWWWWWK....",
      "KpqKWWKWWWWWK...",
      "KqpKWWWWWWWWWK..",
      "KpqKWWWWWWWWWWK.",
      "KqpKWWWWWKKPWWK.",
      "KpKWWWWWK.KWWWK.",
      ".KKWWWWWK..KKK..",
      "..KWWWWWWK......",
      "..KWWWWWWWK.....",
      "..KKKKKKKKK.....",
    ],
  },
  {
    id: "shark",
    name: "Shark",
    rows: [
      "................",
      "................",
      ".......KK.......",
      "......KBBK......",
      ".....KBBBK......",
      "KK..KBBBBBKKKK..",
      "KbKKBBBBBBBBBBKK",
      "KbbBBBBBBBBBWKBK",
      ".KbBBBBBBBBBBBBK",
      "KbbBWWWWWWWWWWKK",
      "KbKKWWWWWWWKWKWK",
      "KK..KWWWWWWWWWK.",
      ".....KKKbKKKKK..",
      ".......KK.......",
      "................",
      "................",
    ],
  },
  {
    id: "octopus",
    name: "Octopus",
    rows: [
      ".....KKKKKK.....",
      "....KppppppK....",
      "...KppqqppppK...",
      "..KppqqppppppK..",
      "..KppppppppppK..",
      "..KpWWKppWWKpK..",
      "..KpWKKppWKKpK..",
      "..KppppppppppK..",
      "..KppPppppPppK..",
      "...KppppppppK...",
      "..KpKpKppKpKpK..",
      ".KpKKpKppKpKKpK.",
      ".KpK.KpKKpK.KpK.",
      "..KpKKpK.KpKpK..",
      "...KK.K...K.KK..",
      "................",
    ],
  },
  {
    id: "bee",
    name: "Bee",
    rows: [
      "................",
      "....KK...KK.....",
      "...KWWK.KWWK....",
      "...KWLWKWLWK....",
      "....KWWKWWK.....",
      "...KKKKKKKKK....",
      "..KGGKKGGKKGK...",
      ".KGGGKKGGKKGGK..",
      "KGWKGKKGGKKGGGKK",
      "KGKKGKKGGKKGGGGK",
      "KGGGGKKGGKKGGGKK",
      ".KGSGKKGGKKGGK..",
      "..KGGKKGGKKGK...",
      "...KKKKKKKKK....",
      "................",
      "................",
    ],
  },
  // ---- space and robots ----
  {
    id: "rocket",
    name: "Rocket",
    rows: [
      ".......KK.......",
      "......KSSK......",
      ".....KSSSSK.....",
      ".....KWWWWK.....",
      "....KWWWWWWK....",
      "....KWWLLWWK....",
      "....KWLBBLWK....",
      "....KWLBBLWK....",
      "....KWWLLWWK....",
      "....KWWWWWWK....",
      "...KSKWWWWKSK...",
      "..KSSKWWWWKSSK..",
      "..KSSKKKKKKSSK..",
      "..KKK.KGGK.KKK..",
      "......KOGK......",
      ".......KK.......",
    ],
  },
  {
    id: "robot",
    name: "Robot",
    rows: [
      ".......KK.......",
      ".......KSK......",
      ".......KK.......",
      "...KKKKKKKKKK...",
      "..KwwwwwwwwwwK..",
      "..KwKKKwwKKKwK..",
      ".KKwKTKwwKTKwKK.",
      "KDKwKKKwwKKKwKDK",
      "KDKwwwwwwwwwwKDK",
      ".KKwwKKKKKKwwKK.",
      "..KwwKGwGwKwwK..",
      "..KwwwKKKKwwwK..",
      "...KKKKKKKKKK...",
      "....KBBBBBBK....",
      "...KBBKGGKBBK...",
      "...KKKKKKKKKK...",
    ],
  },
  {
    id: "ufo",
    name: "UFO",
    rows: [
      "................",
      "................",
      "......KKKK......",
      ".....KLLWLK.....",
      "....KLLLLWLK....",
      "....KLLLLLLK....",
      "..KKKKKKKKKKKK..",
      ".KDDwwwwwwwwDDK.",
      "KDDGDDGDDGDDGDDK",
      ".KDDwwwwwwwwDDK.",
      "..KKKKKKKKKKKK..",
      "....KGGGGGGK....",
      "...KYYGYYGYYK...",
      "..KYYYYYYYYYYK..",
      ".KYYYYYYYYYYYYK.",
      "................",
    ],
  },
  {
    id: "planet",
    name: "Ringed planet",
    rows: [
      "................",
      "............KK..",
      ".....KKKKKKKGK..",
      "....KbbbbbKGGK..",
      "...KbLLbbbGGbK..",
      "..KbLbbbbGGbbbK.",
      "..KbbbbbGGbbbbK.",
      "..KbBBBGGBBBBBK.",
      "..KbbbGGbbbbbbK.",
      "..KbbGGbbbbbbbK.",
      "..KBGGBBBBBBBK..",
      ".KGGKbbbbbbbK...",
      ".KGKKKBBBBBK....",
      ".KK...KKKKK.....",
      "................",
      "................",
    ],
  },
  {
    id: "astronaut",
    name: "Astronaut",
    rows: [
      ".....KKKKKK.....",
      "....KWWWWWWK....",
      "...KWWWWWWWWK...",
      "..KWWKKKKKKWWK..",
      "..KWKBBBBLBKWK..",
      "..KWKBBBLLBKWK..",
      "..KWKBBBBBBKWK..",
      "..KWWKKKKKKWWK..",
      "...KWWWWWWWWK...",
      "..KKKwwwwwwKKK..",
      ".KWWKwSwBwGKWWK.",
      ".KWKKwwwwwwKKWK.",
      ".KwK.KwwwwK.KwK.",
      "..K..KwKKwK..K..",
      ".....KWKKWK.....",
      ".....KKK.KKK....",
    ],
  },
  {
    id: "star",
    name: "Star",
    rows: [
      ".......KK.......",
      "......KGGK......",
      "......KYGK......",
      ".....KGYGGK.....",
      "KKKKKKGYGGKKKKKK",
      "KGYYGGGGGGGGGGGK",
      ".KGGGGKGGKGGGGK.",
      "..KGGGKGGKGGGK..",
      "...KGGGGGGGGK...",
      "...KGSGKKGSGK...",
      "..KGGGGGGGGGGK..",
      "..KGGGGKKGGGGK..",
      ".KGGGKK..KKGGGK.",
      ".KGKK......KKGK.",
      ".KK..........KK.",
      "................",
    ],
  },
  // ---- games and tech ----
  {
    id: "controller",
    name: "Game controller",
    rows: [
      "................",
      "................",
      "................",
      "..KKKKKKKKKKKK..",
      ".KbbbbbbbbbbbbK.",
      "KbbbKbbbbbbbSbbK",
      "KbbKKKbbbbbSbGbK",
      "KbbbKbbKbKbbGbbK",
      "KbbbbbbbbbbbbbbK",
      "KbbbbbKKKKbbbbbK",
      "KbbbbK....KbbbbK",
      ".KbbK......KbbK.",
      "..KK........KK..",
      "................",
      "................",
      "................",
    ],
  },
  {
    id: "ghost",
    name: "Ghost",
    rows: [
      "................",
      ".....KKKKKK.....",
      "...KKWWWWWWKK...",
      "..KWWWWWWWWWWK..",
      ".KWWWWWWWWWWWWK.",
      ".KWWWKWWWWKWWWK.",
      ".KWWWKWWWWKWWWK.",
      "KWWPPWWWWWWPPWWK",
      "KWWWWWWKKWWWWWWK",
      "KWWWWWWKKWWWWWWK",
      "KWWWWWWWWWWWWWWK",
      "KwWWWWWWWWWWWWwK",
      "KwwWWWWWWWWWWwwK",
      "KwwwKKwwwwKKwwwK",
      "KwwK..KwwK..KwwK",
      ".KK....KK....KK.",
    ],
  },
  {
    id: "slime",
    name: "Slime",
    rows: [
      "................",
      "................",
      "................",
      ".......KK.......",
      "......KEEK......",
      ".....KEWEEK.....",
      "....KEWEEEEK....",
      "...KEEEEEEEEK...",
      "..KEEEKEEKEEEK..",
      "..KEEEKEEKEEEK..",
      ".KEEEEEEEEEEEEK.",
      ".KEEEEKEEKEEEEK.",
      "KEEEEEEKKEEEEEEK",
      "KeEEEEEEEEEEEEeK",
      ".KeeeeeeeeeeeeK.",
      "..KKKKKKKKKKKK..",
    ],
  },
  {
    id: "sword",
    name: "Sword",
    rows: [
      ".............KK.",
      "............KWWK",
      "...........KWwwK",
      "..........KWwwK.",
      ".........KWwwK..",
      "........KWwwK...",
      ".......KWwwK....",
      "..KK..KWwwK.....",
      "..KGK.KwwK......",
      "...KGKwwK.......",
      "....KGKK........",
      "....KcKGK.......",
      "...KcK.KGK......",
      "..KcK...KK......",
      ".KGK............",
      ".KK.............",
    ],
  },
  {
    id: "shield",
    name: "Shield",
    rows: [
      "................",
      ".KKKKKKKKKKKKKK.",
      ".KGGGGGGGGGGGGK.",
      ".KGBBBBBbbbbbGK.",
      ".KGBBBBBbbbbbGK.",
      ".KGBBBBWWbbbbGK.",
      ".KGBBBWWWWbbbGK.",
      ".KGBBWWBbWWbbGK.",
      ".KGBWWBBbbWWbGK.",
      "..KGWBBBbbbWGK..",
      "..KGBBBBbbbbGK..",
      "...KGBBBbbbGK...",
      "....KGBBbbGK....",
      ".....KGBbGK.....",
      "......KGGK......",
      ".......KK.......",
    ],
  },
  {
    id: "crown",
    name: "Crown",
    rows: [
      "................",
      "................",
      "................",
      ".KK....KK....KK.",
      "KRRK..KTTK..KRRK",
      ".KK...KTTK...KK.",
      ".KGK.KGKKGK.KGK.",
      ".KGGKGGGGGGKGGK.",
      ".KGGGGGYYGGGGGK.",
      ".KGGGGYGGYGGGGK.",
      ".KGGGGGGGGGGGGK.",
      ".KgGRGgTTgGRGgK.",
      ".KggggggggggggK.",
      ".KKKKKKKKKKKKKK.",
      "................",
      "................",
    ],
  },
  {
    id: "gem",
    name: "Gem",
    rows: [
      "................",
      "................",
      "...KKKKKKKKKK...",
      "..KTWTTTTTTtTK..",
      ".KTWWTTTTTTttTK.",
      "KKKKKKKKKKKKKKKK",
      "KTTWTTTTTTTTttTK",
      ".KTTWTTTTTTttTK.",
      "..KTTWTTTTttTK..",
      "...KTTTTTttTK...",
      "....KTTTTttK....",
      ".....KTTttK.....",
      "......KTtK......",
      ".......KK.......",
      "................",
      "................",
    ],
  },
  {
    id: "heart",
    name: "Heart",
    rows: [
      "................",
      "................",
      "..KKKK....KKKK..",
      ".KRRRRK..KRRRRK.",
      "KRWWRRRKKRRRRRRK",
      "KRWRRRRRRRRRRRRK",
      "KRRRRRRRRRRRRRRK",
      "KRRRRRRRRRRRRRRK",
      ".KRRRRRRRRRRRRK.",
      "..KRRRRRRRRRRK..",
      "...KRRRRRRRRK...",
      "....KRRRRRRK....",
      ".....KRRRRK.....",
      "......KRRK......",
      ".......KK.......",
      "................",
    ],
  },
  {
    id: "trophy",
    name: "Trophy",
    rows: [
      "................",
      "..KKKKKKKKKKKK..",
      "KKKGYYGGGGGGGKKK",
      "KGKGYGGGGGGGgKGK",
      "KGKGYGGGGGGGgKGK",
      "KGKGGGGGGGGGgKGK",
      ".KGKGGGGGGGgKGK.",
      "..KKGGGGGGGgKK..",
      "....KGGGGGgK....",
      ".....KGGGgK.....",
      "......KGgK......",
      "......KGgK......",
      ".....KGGGgK.....",
      "....KNNNNNNK....",
      "....KNGGGGNK....",
      "....KKKKKKKK....",
    ],
  },
  // ---- nature ----
  {
    id: "tree",
    name: "Tree",
    rows: [
      "......KKKK......",
      "....KKEEEEKK....",
      "...KEEEEEEEEK...",
      "..KEEEEEEEEeEK..",
      ".KEEEEeEEEEEEEK.",
      ".KEEEEEEEEEeEEK.",
      "KEEeEEEEEEEEEeEK",
      "KEEEEEEEeEEEEEEK",
      "KeEEEEEEEEEEEEeK",
      ".KeeEEEEEEEEeeK.",
      "..KKeeeeeeeeKK..",
      "....KKKncKKK....",
      "......KncK......",
      "......KncK......",
      ".....KnnccK.....",
      "....KKKKKKKK....",
    ],
  },
  {
    id: "sun",
    name: "Sun",
    rows: [
      ".......OO.......",
      "..O....OO....O..",
      "...O........O...",
      "......KKKK......",
      "....KKGGGGKK....",
      "...KGYYGGGGGK...",
      "...KGYGGGGGGK...",
      "OO.KGGKGGKGGK.OO",
      "OO.KGGKGGKGGK.OO",
      "...KGGGGGGGGK...",
      "...KGSKGGKSGK...",
      "....KKGKKGKK....",
      "......KKKK......",
      "...O........O...",
      "..O....OO....O..",
      ".......OO.......",
    ],
  },
  {
    id: "lightning",
    name: "Lightning bolt",
    rows: [
      "........KKKKKK..",
      ".......KGGGGK...",
      "......KGYGGK....",
      ".....KGYGGK.....",
      "....KGYGGK......",
      "...KGYGGKKKKKK..",
      "..KGGGGGGGGGK...",
      ".KKKKKKGGGGK....",
      "......KGGGK.....",
      ".....KGGGK......",
      "....KGGGK.......",
      "...KGGGK........",
      "..KGGK..........",
      ".KGK............",
      ".KK.............",
      "................",
    ],
  },
  {
    id: "flame",
    name: "Flame",
    rows: [
      ".......KK.......",
      "......KRK.......",
      "......KRRK......",
      ".....KRRRK..K...",
      "....KRRORRK.KK..",
      "...KRRROORK.KRK.",
      "...KRROOORRKRRK.",
      "..KRROOOOORRRRK.",
      "..KRROOGGOORRRK.",
      ".KRROOGGGGOORRK.",
      ".KRROGGYYGGORRK.",
      ".KRROGYYYYGORRK.",
      ".KRROGYYYYGORRK.",
      "..KRROGGGGORRK..",
      "...KKRRRRRRKK...",
      ".....KKKKKK.....",
    ],
  },
  {
    id: "snowflake",
    name: "Snowflake",
    rows: [
      ".......bb.......",
      ".....b.LL.b.....",
      "......bLLb......",
      "..b....LL....b..",
      "...bb..LL..bb...",
      "....LL.LL.LL....",
      ".....LLLLLL.....",
      "bLLLLLLWWLLLLLLb",
      "bLLLLLLWWLLLLLLb",
      ".....LLLLLL.....",
      "....LL.LL.LL....",
      "...bb..LL..bb...",
      "..b....LL....b..",
      "......bLLb......",
      ".....b.LL.b.....",
      ".......bb.......",
    ],
  },
];

export const SPRITE_IDS: readonly string[] = SPRITES.map((s) => s.id);

export function spriteById(id: string | null | undefined): Sprite | undefined {
  return id ? SPRITES.find((s) => s.id === id) : undefined;
}

export const isSpriteId = (id: unknown): id is string => typeof id === "string" && SPRITE_IDS.includes(id);

/** Horizontal runs of one colour, so the SVG has a few dozen rects instead of 256. */
export function spriteRuns(sprite: Sprite): { x: number; y: number; w: number; c: string }[] {
  const runs: { x: number; y: number; w: number; c: string }[] = [];
  sprite.rows.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      const ch = row[x]!;
      let end = x + 1;
      while (end < row.length && row[end] === ch) end++;
      if (ch !== ".") runs.push({ x, y, w: end - x, c: SPRITE_PALETTE[ch] ?? "#f0f" });
      x = end;
    }
  });
  return runs;
}
